import {priority, common} from '@priority-software/priority-proto-api/lib';
import {getSettingsKey} from './utils';
import {handleScreenMessage} from '../../../actions/screen';
import {fieldUpdate} from '../../../actions/standAloneForm';
import {fetchFormData} from '../../../../../services/form/form';
import {Form, formsStore, Record, Field} from '../../../../../stores/Form';
import {Column} from '../../../../../stores/Form';
import {
    defaultSidePanelOptions,
    fetchMax,
    SettingsPanelCol,
    MonitoredActions,
    subformTypeTitles,
    monitorPromoPretext,
    MonitoredPromoActions
} from './consts';
import {FileFields, FileStatus} from '../Attachments/consts';
import {Filter} from '../../../../../stores/Form/Filter';
import {
    getServerStoreData,
    setServerStoreData
} from '../../../../../services/proto/ServerStorage/service';
import {fieldValue} from '../../../../../stores/Form/Field';
import {mapValueByColumnType} from '../../../../../stores/Form/utils';
import {sendMonitor} from '../../../../../utils/monitoring';
import {featurePopoverRef} from '../../../../FeaturePopover/FeaturePopover.root';
import {featuresIDs, shouldShowFeature} from '../../../../FeaturePopover/constants/map';
import {PopoverContent} from '../../../../FeaturePopover/FeaturePopover';
import texts from '../texts';

export type monitorActionProps = {
    subformType: priority.netitems.form.SubformType;
    action: MonitoredActions;
    subformName: string;
};

type FeatureData = {
    featureID: string;
    type: priority.netitems.form.SubformType;
    triggerRef: React.MutableRefObject<any>;
    consequentSubformType?: priority.netitems.form.SubformType;
};

export const fetchMore = async (form: Form, files: Record[]) => {
    const {session} = form;
    const data: priority.netitems.form.FetchRs = await fetchFormData({
        session,
        top: fetchMax,
        skip: files.length
    });
    return await handleScreenMessage(data, {}, () => {
        if (data.Records?.length) {
            form.mergeRecords(files, data.Records);
        }
        if (!data.Records?.length || data.Records?.length % fetchMax !== 0) {
            form.setShouldFetchMore(false);
        }
    });
};

export const handleSearch = async (session: string, searchQuery: string, columns: Column[]) => {
    const newFilter = new Filter();
    const currentForm = formsStore.currentScreen.getActiveForm();
    if (searchQuery) {
        columns.forEach(column => {
            newFilter.Condition.push({
                ColumnID: column.id,
                Operator: priority.netitems.shared.ComparisonOperator.Eq,
                Values: [`*${searchQuery}*`],
                IgnoreCase: true
            });
        });
        newFilter.Operator = priority.netitems.shared.LogicalOperator.Or;
    }
    const data: priority.netitems.form.FetchRs = await fetchFormData({
        session,
        filters: searchQuery ? [newFilter] : [],
        top: fetchMax + 1,
        options: {keepFocus: true}
    });
    currentForm.setRecords(data.Records);
    currentForm.setFastSearchQuery(searchQuery);
    currentForm.setShouldFetchMore(data.Records?.length > fetchMax);
};

export const getEnabledColumns = async (formName: string) => {
    if (!formName) {
        return;
    }
    const key = getSettingsKey(formName);
    const res = await getServerStoreData([key], false, true);
    if (res[key]) {
        return JSON.parse(res[key].Str);
    } else {
        return [];
    }
};

export const setEnabledColumns = (formName: string, cols: SettingsPanelCol[]): string[] => {
    if (!formName) {
        return null;
    }
    const key = getSettingsKey(formName);
    const currEnabled = [];
    cols.forEach(col => {
        if (col.isChecked) {
            currEnabled.push(col.name);
        }
    });
    const storeMap = {[key]: new common.Constant({Str: JSON.stringify(currEnabled)})};
    setServerStoreData(storeMap, true, false);
    return currEnabled;
};

export const saveRow = async (
    column: Column,
    file: Record,
    mappedField: priority.netitems.form.IField
) => {
    const field = file.getField(column.id);
    const isSuccess = await fieldUpdate(file.recordIndex, mappedField, defaultSidePanelOptions);
    if (!isSuccess) {
        if (file.isNew) {
            return isSuccess;
        }
        field.setCurrentValue(field.origValue);
        await fieldUpdate(
            file.recordIndex,
            mapValueByColumnType(field.currentValue, column),
            defaultSidePanelOptions
        );
    }
    const screen = formsStore.currentScreen;
    return screen.saveRecord(defaultSidePanelOptions);
};

export const onSaveChanges = (fieldName: string, record: Record, value: fieldValue) => {
    const column: Column = record.parentForm.getColumnByName(fieldName);
    const columnId = column.id;
    const field: Field = record.fieldsMap.get(columnId);
    if (fieldName === FileFields.status) {
        field.setCurrentValue(value ? FileStatus.readOnly : '');
    } else {
        field.setCurrentValue(value);
    }
    const currentForm = formsStore.currentScreen?.getActiveForm();
    currentForm?.setCurrentRow(record.recordIndex);
    saveRow(column, record, mapValueByColumnType(field.currentValue, column));
};

export const monitorAction = ({subformType, action, subformName}: monitorActionProps) => {
    sendMonitor(`${subformTypeTitles[subformType]}${action}`, null, null, subformName);
};

const getPromoTitles = (subformType: priority.netitems.form.SubformType) => {
    if (subformType === priority.netitems.form.SubformType.Attachments) {
        return 'Attachments';
    } else {
        return 'History';
    }
};

const monitorPromoAction = (
    subformType: priority.netitems.form.SubformType,
    action: MonitoredPromoActions
) => {
    const promoTitle = getPromoTitles(subformType);
    sendMonitor(`${monitorPromoPretext}${promoTitle}${action}`);
};

const getPopoverContent = (isAttachments: boolean): PopoverContent => {
    const content = isAttachments ? texts.promo_attachments : texts.promo_history;
    return {
        title: texts.promo_title,
        content,
        secondaryButtonText: texts.promo_confirm,
        primaryButtonText: texts.promo_show
    };
};

const openPanelFromPromo = (
    subformType: priority.netitems.form.SubformType,
    openSubForm: (type: priority.netitems.form.SubformType) => void
) => {
    openSubForm(subformType);
    monitorPromoAction(subformType, MonitoredPromoActions.Show);
};

const showConsequentPromos = (
    data: FeatureData,
    openSubForm: (type: priority.netitems.form.SubformType) => void,
    historyButtonsRef: React.MutableRefObject<any>
) => {
    openSubForm(data.type);
    monitorPromoAction(data.type, MonitoredPromoActions.Show);
    const popoverContent = getPopoverContent(false);

    setTimeout(
        featurePopoverRef?.current?.showFeaturePopover,
        3000,
        featuresIDs.sidePanelHistory,
        historyButtonsRef?.current,
        () => monitorPromoAction(data.consequentSubformType, MonitoredPromoActions.Close),
        () => openPanelFromPromo(data.consequentSubformType, openSubForm),
        popoverContent
    );
};

const getConsequentSubformType = (): priority.netitems.form.SubformType => {
    const statusLogType = priority.netitems.form.SubformType.StatusLog;
    const changeLogType = priority.netitems.form.SubformType.ChangeLog;
    if (formsStore.sidePanelSubforms[statusLogType]) {
        return statusLogType;
    } else if (formsStore.sidePanelSubforms[changeLogType]) {
        return changeLogType;
    } else {
        return null;
    }
};

export const handleFeaturePromotion = ({
    attachmentsButtonRef,
    historyButtonsRef,
    initialHistorySubformType,
    openSubForm
}: {
    attachmentsButtonRef: React.MutableRefObject<any>;
    historyButtonsRef: React.MutableRefObject<any>;
    initialHistorySubformType: priority.netitems.form.SubformType;
    openSubForm: (type: priority.netitems.form.SubformType) => void;
}) => {
    let featureData: FeatureData;
    const shouldShowAttachmentsPromo =
        attachmentsButtonRef.current && shouldShowFeature(featuresIDs.sidePanelAttachments);
    const shouldShowHistoryPromo =
        historyButtonsRef.current && shouldShowFeature(featuresIDs.sidePanelHistory);

    if (shouldShowAttachmentsPromo) {
        const consequentSubformType = shouldShowHistoryPromo && getConsequentSubformType();
        featureData = {
            featureID: featuresIDs.sidePanelAttachments,
            type: priority.netitems.form.SubformType.Attachments,
            triggerRef: attachmentsButtonRef?.current,
            consequentSubformType
        };
    } else if (shouldShowHistoryPromo) {
        featureData = {
            featureID: featuresIDs.sidePanelHistory,
            type: initialHistorySubformType,
            triggerRef: historyButtonsRef?.current
        };
    }

    if (featureData) {
        const popoverContent = getPopoverContent(shouldShowAttachmentsPromo);
        const confirmButtonHandler = featureData.consequentSubformType
            ? () => showConsequentPromos(featureData, openSubForm, historyButtonsRef)
            : () => openPanelFromPromo(featureData.type, openSubForm);

        featurePopoverRef?.current?.showFeaturePopover(
            featureData.featureID,
            featureData.triggerRef,
            () => monitorPromoAction(featureData.type, MonitoredPromoActions.Close),
            confirmButtonHandler,
            popoverContent
        );
    }
};

export const closeFeaturePopover = () => {
    featurePopoverRef?.current?.closeById(featuresIDs.sidePanelAttachments);
    featurePopoverRef?.current?.closeById(featuresIDs.sidePanelHistory);
};
